import {
  Box,
  Checkbox,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import {
  DEFAULT_DEV_FACTORS,
  DEFAULT_INCLUDE,
  DEFAULT_YEAR_COUNT,
} from "components/lossRating/lossRatingConstants";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as programActions from "store/actions/input/program/programActions";
import * as programSelectors from "store/selectors/input/program/programSelectors";

const FONT_SIZE = "0.85rem";

const SECTIONS = {
  gl: {
    label: "General Liability",
    getData: programSelectors.getLossRating,
    update: programActions.updateLossRating,
  },
  auto: {
    label: "Auto Liability",
    getData: programSelectors.getAutoLossRating,
    update: programActions.updateAutoLossRating,
  },
};

const TAB_ORDER = ["gl", "auto"];

const cellSx = { fontSize: FONT_SIZE, py: 0.5, px: 1 };
const headSx = {
  fontSize: FONT_SIZE,
  fontWeight: 600,
  py: 0.75,
  px: 1,
  whiteSpace: "nowrap",
};

const TF = {
  variant: "filled",
  size: "small",
  fullWidth: true,
  InputLabelProps: { shrink: true },
};

const Section = ({ title, children }) => (
  <Paper variant={"outlined"} sx={{ p: 2 }}>
    <Stack spacing={2}>
      <Typography variant={"subtitle1"} fontWeight={600}>
        {title}
      </Typography>
      {children}
    </Stack>
  </Paper>
);

const getDefaultYear = (i) => ({
  include: DEFAULT_INCLUDE[i] ?? true,
  exposure: null,
  openClaimCount: null,
  closedClaimCount: null,
  incurredOpen: null,
  incurredClosed: null,
  developmentFactor: DEFAULT_DEV_FACTORS[i] ?? 1.0,
});

const getEffectiveYears = (stored, projectedExposure) => {
  const years = [];
  for (let i = 0; i < DEFAULT_YEAR_COUNT; i++) {
    const stored_yr = stored?.[i];
    years.push({
      ...getDefaultYear(i),
      ...(stored_yr ?? {}),
    });
  }
  return years;
};

const computeDerived = (yr, projectedExposure, annualTrend, yearIndex) => {
  const totalCount = (yr.openClaimCount ?? 0) + (yr.closedClaimCount ?? 0);
  const totalIncurred = (yr.incurredOpen ?? 0) + (yr.incurredClosed ?? 0);
  const exposure = yr.exposure ?? projectedExposure ?? 0;
  const trend = Math.pow(annualTrend ?? 1.08, yearIndex);
  const dev = yr.developmentFactor ?? DEFAULT_DEV_FACTORS[yearIndex - 1] ?? 1.0;
  const tAndD = trend * dev;
  const expFactor = exposure > 0 ? (projectedExposure ?? 0) / exposure : 1;
  const ultimateLoss = totalIncurred * tAndD * expFactor;
  return { totalCount, totalIncurred, trend, tAndD, expFactor, ultimateLoss };
};

const LossRatingTable = ({ variant }) => {
  const section = SECTIONS[variant];
  const dispatch = useDispatch();
  const data = useSelector(section.getData);

  const projectedExposure = data.projectedExposure ?? null;
  const annualTrend = data.annualTrendFactor ?? 1.08;
  const years = getEffectiveYears(data.policyYears, projectedExposure);

  const updateTop = useCallback(
    (field, value) => dispatch(section.update(field, value)),
    [dispatch, section]
  );

  const updateYear = useCallback(
    (i, field, value) => {
      const updated = years.map((y, idx) =>
        idx === i ? { ...y, [field]: value } : y
      );
      dispatch(section.update("policyYears", updated));
    },
    [dispatch, section, years]
  );

  const totals = years.reduce(
    (acc, yr) => ({
      open: acc.open + (yr.openClaimCount ?? 0),
      closed: acc.closed + (yr.closedClaimCount ?? 0),
      incurredOpen: acc.incurredOpen + (yr.incurredOpen ?? 0),
      incurredClosed: acc.incurredClosed + (yr.incurredClosed ?? 0),
    }),
    { open: 0, closed: 0, incurredOpen: 0, incurredClosed: 0 }
  );
  const n = years.length;
  const avgs = {
    open: totals.open / n,
    closed: totals.closed / n,
    incurredOpen: totals.incurredOpen / n,
    incurredClosed: totals.incurredClosed / n,
  };

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Section title={"Rating Parameters"}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Disabled ifReadOnly>
              <PrettyNumberTextField
                {...TF}
                label={"Projected Exposure ($)"}
                value={data.projectedExposure ?? null}
                onChangeNumber={(v) => updateTop("projectedExposure", v)}
              />
            </Disabled>
          </Grid>
        </Grid>
      </Section>

      <Section title={"Policy Year Loss Data"}>
        <TableContainer>
          <Table size={"small"} sx={{ tableLayout: "auto" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={headSx}>{"Year"}</TableCell>
                <TableCell sx={headSx} align={"center"}>
                  {"Include"}
                </TableCell>
                <TableCell sx={headSx} align={"right"}>
                  {"Exposure ($)"}
                </TableCell>
                <TableCell sx={headSx} align={"right"}>
                  {"Open Claims"}
                </TableCell>
                <TableCell sx={headSx} align={"right"}>
                  {"Closed Claims"}
                </TableCell>
                <TableCell
                  sx={{ ...headSx, color: "text.secondary" }}
                  align={"right"}
                >
                  {"Total Claims"}
                </TableCell>
                <TableCell sx={headSx} align={"right"}>
                  {"Incurred Open ($)"}
                </TableCell>
                <TableCell sx={headSx} align={"right"}>
                  {"Incurred Closed ($)"}
                </TableCell>
                <TableCell
                  sx={{ ...headSx, color: "text.secondary" }}
                  align={"right"}
                >
                  {"Total Incurred"}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {years.map((yr, i) => {
                const yearIndex = i + 1;
                const policyYear = new Date().getFullYear() - yearIndex;
                const derived = computeDerived(
                  yr,
                  projectedExposure,
                  annualTrend,
                  yearIndex
                );
                const rowSx = yr.include ? {} : { opacity: 0.45 };
                const inputSx = {
                  "& .MuiInputBase-input": {
                    fontSize: FONT_SIZE,
                    textAlign: "right",
                    py: "4px",
                    px: 1,
                  },
                };

                return (
                  <TableRow key={i} hover sx={rowSx}>
                    <TableCell sx={cellSx}>
                      <Typography fontSize={FONT_SIZE} color={"text.secondary"}>
                        {policyYear}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, textAlign: "center" }}>
                      <Disabled ifReadOnly>
                        <Checkbox
                          size={"small"}
                          checked={yr.include !== false}
                          onChange={(e) =>
                            updateYear(i, "include", e.target.checked)
                          }
                          sx={{ p: 0.5 }}
                        />
                      </Disabled>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 110 }}>
                      <Disabled ifReadOnly>
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          value={yr.exposure ?? projectedExposure ?? null}
                          onChangeNumber={(v) => updateYear(i, "exposure", v)}
                          sx={inputSx}
                        />
                      </Disabled>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 90 }}>
                      <Disabled ifReadOnly>
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          value={yr.openClaimCount ?? null}
                          onChangeNumber={(v) =>
                            updateYear(i, "openClaimCount", v)
                          }
                          sx={inputSx}
                        />
                      </Disabled>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 90 }}>
                      <Disabled ifReadOnly>
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          value={yr.closedClaimCount ?? null}
                          onChangeNumber={(v) =>
                            updateYear(i, "closedClaimCount", v)
                          }
                          sx={inputSx}
                        />
                      </Disabled>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellSx,
                        minWidth: 80,
                        textAlign: "right",
                        color: "text.secondary",
                      }}
                    >
                      {derived.totalCount || ""}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 120 }}>
                      <Disabled ifReadOnly>
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          value={yr.incurredOpen ?? null}
                          onChangeNumber={(v) =>
                            updateYear(i, "incurredOpen", v)
                          }
                          sx={inputSx}
                        />
                      </Disabled>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 120 }}>
                      <Disabled ifReadOnly>
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          value={yr.incurredClosed ?? null}
                          onChangeNumber={(v) =>
                            updateYear(i, "incurredClosed", v)
                          }
                          sx={inputSx}
                        />
                      </Disabled>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellSx,
                        minWidth: 100,
                        textAlign: "right",
                        color: "text.secondary",
                      }}
                    >
                      {derived.totalIncurred || ""}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow
                sx={{
                  borderTop: "2px solid",
                  borderColor: "divider",
                  backgroundColor: "grey.50",
                }}
              >
                <TableCell sx={{ ...cellSx, fontWeight: 600 }} colSpan={2}>
                  {"Sum"}
                </TableCell>
                <TableCell sx={cellSx} />
                <TableCell
                  sx={{ ...cellSx, textAlign: "right", fontWeight: 600 }}
                >
                  {totals.open || ""}
                </TableCell>
                <TableCell
                  sx={{ ...cellSx, textAlign: "right", fontWeight: 600 }}
                >
                  {totals.closed || ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    textAlign: "right",
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  {totals.open + totals.closed || ""}
                </TableCell>
                <TableCell
                  sx={{ ...cellSx, textAlign: "right", fontWeight: 600 }}
                >
                  {totals.incurredOpen > 0
                    ? totals.incurredOpen.toLocaleString()
                    : ""}
                </TableCell>
                <TableCell
                  sx={{ ...cellSx, textAlign: "right", fontWeight: 600 }}
                >
                  {totals.incurredClosed > 0
                    ? totals.incurredClosed.toLocaleString()
                    : ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    textAlign: "right",
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  {totals.incurredOpen + totals.incurredClosed > 0
                    ? (
                        totals.incurredOpen + totals.incurredClosed
                      ).toLocaleString()
                    : ""}
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell
                  sx={{
                    ...cellSx,
                    fontStyle: "italic",
                    color: "text.secondary",
                  }}
                  colSpan={2}
                >
                  {"Avg."}
                </TableCell>
                <TableCell sx={cellSx} />
                <TableCell
                  sx={{
                    ...cellSx,
                    textAlign: "right",
                    fontStyle: "italic",
                    color: "text.secondary",
                  }}
                >
                  {avgs.open > 0 ? avgs.open.toFixed(1) : ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    textAlign: "right",
                    fontStyle: "italic",
                    color: "text.secondary",
                  }}
                >
                  {avgs.closed > 0 ? avgs.closed.toFixed(1) : ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    textAlign: "right",
                    fontStyle: "italic",
                    color: "text.secondary",
                  }}
                >
                  {avgs.open + avgs.closed > 0
                    ? (avgs.open + avgs.closed).toFixed(1)
                    : ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    textAlign: "right",
                    fontStyle: "italic",
                    color: "text.secondary",
                  }}
                >
                  {avgs.incurredOpen > 0
                    ? Math.round(avgs.incurredOpen).toLocaleString()
                    : ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    textAlign: "right",
                    fontStyle: "italic",
                    color: "text.secondary",
                  }}
                >
                  {avgs.incurredClosed > 0
                    ? Math.round(avgs.incurredClosed).toLocaleString()
                    : ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    textAlign: "right",
                    fontStyle: "italic",
                    color: "text.secondary",
                  }}
                >
                  {avgs.incurredOpen + avgs.incurredClosed > 0
                    ? Math.round(
                        avgs.incurredOpen + avgs.incurredClosed
                      ).toLocaleString()
                    : ""}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography
          variant={"caption"}
          color={"text.secondary"}
          sx={{ mt: 0.5 }}
        >
          {
            "Trend factor = Annual Trend ^ year index (Y-1 = most recent). Development factors default to Excel values."
          }
        </Typography>
      </Section>
    </Stack>
  );
};

const LossRating = () => {
  const [tab, setTab] = useState(0);
  const variant = TAB_ORDER[tab];

  return (
    <Component title={"Loss Rating"}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              fontSize: "0.8rem",
              textTransform: "none",
              fontWeight: 500,
              px: 2,
              py: 0.75,
            },
          }}
        >
          {TAB_ORDER.map((key) => (
            <Tab key={key} label={SECTIONS[key].label} disableRipple />
          ))}
        </Tabs>
      </Box>
      {/* Both stay mounted so each section's state survives a tab switch. */}
      {TAB_ORDER.map((key) => (
        <Box key={key} sx={{ display: key === variant ? "block" : "none" }}>
          <LossRatingTable variant={key} />
        </Box>
      ))}
    </Component>
  );
};

export default LossRating;
